import { Button, Form, Input, InputNumber, Modal } from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect } from "react";
import { $framework, useFeedback, useTranslation } from "../../..";

type Props = {};

type ImportFormValues = {
  masterKey: string;
  appId: string;
  publicUrl: string;
  className: string;
  limit?: number;
  skip?: number;
};

type DataType = {
  results: Array<any>;
};

const rules = [{ required: true }];

export const ImportForm = observer((props: Props) => {
  const t = useTranslation();
  const [open, setOpen] = React.useState(false);
  const submitButtonRef = React.useRef<HTMLButtonElement>(null);
  const [loading, setLoading] = React.useState(false);
  const { handleLoadingFeedback, message } = useFeedback();

  useEffect(() => {
    $framework.commands.registerCommand({
      id: "core.parse.importer.open",
      parent: "opendash:debug",
      title: t("opendash:parse.importer.command.title"),
      icon: "import",
      handler: () => setOpen(true),
    });
  }, []);

  async function handleSubmit(values: ImportFormValues) {
    setLoading(true);
    const limit = values.limit || 1_000_000;
    const skip = values.skip || 0;

    const response = await handleLoadingFeedback(
      () =>
        fetch(
          // batch size of 1 million. reduce if you have performance issues.
          // also make sure to iterate with a skip
          `${values.publicUrl}/classes/${values.className}?limit=${limit}&skip=${skip}`,
          {
            method: "GET",
            headers: {
              "X-Parse-Application-Id": values.appId,
              "X-Parse-Master-Key": values.masterKey,
            },
          }
        ),
      "Fetching data from Parse",
      "Data fetched successfully",
      "Error fetching data"
    );

    const data: DataType = await handleLoadingFeedback(
      () => response.json(),
      "Parsing data",
      "Data parsed successfully",
      "Error parsing data"
    );

    try {
      const closeInfo = message.info("Processing data...", -1);
      const parseObjects = data.results.map((item) => {
        const parseObject = new Parse.Object(values.className);
        Object.keys(item).forEach((key) => {
          if (key === "objectId") return;

          parseObject.set(key, item[key]);
        });

        return parseObject;
      });
      closeInfo();

      await handleLoadingFeedback(
        () => Parse.Object.saveAll(parseObjects),
        "Saving data to Parse",
        "Data saved successfully",
        "Error saving data"
      );
    } catch (error) {
      console.error("Error processing data:", error);
    }

    setLoading(false);
  }

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      onOk={() => {
        if (submitButtonRef.current) submitButtonRef.current.click();
      }}
      okText="Import"
      title={t("opendash:parse.importer.command.title")}
      okButtonProps={{ loading: loading }}
    >
      <Form<ImportFormValues>
        name="import-form"
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item<ImportFormValues>
          name="masterKey"
          label="Master Key"
          rules={rules}
        >
          <Input />
        </Form.Item>

        <Form.Item<ImportFormValues> name="appId" label="App ID" rules={rules}>
          <Input />
        </Form.Item>

        <Form.Item<ImportFormValues>
          name="publicUrl"
          label="Public URL"
          rules={rules}
        >
          <Input />
        </Form.Item>

        <Form.Item<ImportFormValues>
          name="className"
          label="Class Name"
          rules={rules}
        >
          <Input />
        </Form.Item>

        <Form.Item<ImportFormValues>
          name="limit"
          label="Limit"
          rules={[{ required: false, type: "number", min: 1 }]}
        >
          <InputNumber />
        </Form.Item>

        <Form.Item<ImportFormValues>
          name="skip"
          label="Skip"
          rules={[{ required: false, type: "number", min: 0 }]}
        >
          <InputNumber />
        </Form.Item>

        <Button
          htmlType="submit"
          style={{ display: "none" }}
          ref={submitButtonRef}
        >
          Importieren
        </Button>
      </Form>
    </Modal>
  );
});
